/**
 * Component-error reporter for the PROVIDER-LESS browser surfaces -- error boundaries and catch sites that no
 * `TelemetryProvider` covers. Non-blocking, fails silently, returns early outside the browser.
 *
 * IT IS NOT A DUPLICATE OF THE PROVIDER'S ERROR CAPTURE, and the overlap question is settled rather than open. Its call
 * sites fall in two classes and NEITHER is reachable by `TelemetryProvider`. The MFE frontends (`mfe/trade`,
 * `mfe/navigator`, `mfe/admin-finance`) and the `apps/federa` title fetch sites mount no provider at all. The ERROR
 * BOUNDARIES -- `components/error/RootErrorBoundary`, `components/error/PropertyFeedErrorBoundary`,
 * `mfe/trade`'s `TradeErrorBoundary` and `components/hooks/useErrorTracking` -- CATCH the error, so `window.onerror`
 * never fires and the provider's listener sees nothing. Deleting this sender loses every one of those reports outright.
 *
 * THE SITES ARE NAMED AS CLASSES RATHER THAN COUNTED, deliberately: this paragraph carried "four of its seven call
 * sites" and was already false by two the day `RootErrorBoundary` landed -- and that one is the consequential caller,
 * since `StandaloneSpaProviders` and `createMFEMount` put it over EVERY spa and MFE root at once. A tally in prose is a
 * claim nothing re-checks; the classes are what the overlap argument actually rests on.
 *
 * It emits the SAME OTLP logs shape to the SAME anonymous door as the provider, through the one shared builder
 * (`const/browser-telemetry-attrs.ts`), so a component error and an uncaught error land as the same record kind and a
 * reader needs one query rather than two.
 *
 * HOMED IN THE SANCTIONED TRANSPORT SUBTREE (`components/lib/api/**`, the `QUAL-COMP-PURE-001` DI/transport
 * exemption) rather than under `tools/`, because a `tools/` module may be imported only by a flow leaf and this
 * sender has no flow consumer at all -- its nine importers are error boundaries, a React hook, two MFE frontends and
 * the `apps/federa` title fetch sites, every one of which already reaches this directory (`api-envelope`,
 * `traced-edge-fetch`, `session-utils`). It is a browser TRANSPORT, and this is where the fleet's browser transports
 * live.
 *
 * NO `session.id` RIDES THESE RECORDS YET, and the reason it does not is now purely that nothing has wired it. The
 * session-id SSOT is `getOrCreateSessionId`, a SIBLING in this directory since the 2026-08-20 relocation -- the old
 * blocker (no production module under `tools/` imports `@federa/components`) died with the move, and the stated cure
 * of graduating that accessor to a `utils/browser/` leaf is no longer needed. Correlation for these rows is still
 * `url.full` plus the timestamp; adding the join is a behaviour change to what lands in the telemetry warehouse and is
 * owned separately, not smuggled into a relocation. Do NOT read the session blob out of `localStorage` to fake it --
 * that is exactly the storage scrape the provider's payload dropped.
 */

import { isBrowser } from '@federa/utils/browser/detection';
import { normalizeError } from '@federa/utils/errors/error-core';
import { STACK_TRACE_CAPS } from '@federa/const/stack-trace-caps';
import { TELEMETRY_ENDPOINT } from '@federa/const/websocket';
import { LOG_EVENTS } from '@federa/const/logging';
import { ACTIVITY_TYPES } from '@federa/const/observability-activity-types';
import { buildBrowserOtlpLogsPayload } from '@federa/const/browser-telemetry-attrs';
import { buildJsonPostInit } from '@federa/const/http-request-init';
import type { TelemetryEvent } from '@federa/types/telemetry-browser-events';

// Module-scoped browser types (Workers tsconfig has no DOM lib); isBrowser() owns the SSR guard.
declare const window: { location: { href: string }; [key: string]: unknown };
declare const navigator: { userAgent: string };

/** Fire-and-forget POST of one OTLP logs export. `keepalive` so a report raised during navigation still leaves. The
 *  payload build sits INSIDE the guard: it reads the page's location and user agent, and a sender called from a catch
 *  must not throw a second failure over the caller's recovery (2026-09-23). */
function sendTelemetry(event: TelemetryEvent): void {
	try {
		const payload = buildBrowserOtlpLogsPayload([event], { url: window.location.href, userAgent: navigator.userAgent });
		fetch(TELEMETRY_ENDPOINT, buildJsonPostInit(payload, { keepalive: true })).catch(() => {
			// Silent: logging here would risk recursion since this IS the telemetry sender
		});
		// Intentional: telemetry is non-critical, failures should not affect app
	} catch {
		// Empty catch - valid per intentional comment above
	}
}

/**
 * `error` is `unknown`, NOT `Error`, and the coercion happens ONCE here. The narrow parameter made every catch-site
 * spell `err instanceof Error ? err : new Error(String(err))` before the call -- a hand-rolled respelling of
 * `normalizeError`, repeated at each boundary. Widening the parameter is the producer-side fix: a caller hands over
 * whatever it caught and the sender decides what an unknown throw looks like on the wire, in one place.
 */
export function trackComponentError(
	componentName: string,
	error: unknown,
	errorInfo?: {
		componentStack?: string;
		digest?: string;
	},
): void {
	// Fail silently if not in browser
	if (!isBrowser()) {
		return;
	}

	const normalized = normalizeError(error);

	sendTelemetry({
		type: ACTIVITY_TYPES.ERROR,
		timestamp: Date.now(),
		// No session join is reachable from this layer -- see the module docstring. Blank drops the attribute entirely
		// rather than emitting an empty one, so a reader never mistakes "unreachable" for "the session was empty".
		sessionId: '',
		data: {
			// The builder promotes these three onto `exception.type` / `exception.message` / `exception.stacktrace` and
			// drops the raw keys, so each fact is single-spelled on the wire. `errorType` carries the producer
			// CLASSIFICATION rather than the JS error class, matching the provider's own 'uncaught' /
			// 'unhandled_rejection' vocabulary -- one meaning for the attribute across both browser producers.
			errorType: LOG_EVENTS.COMPONENT_ERROR,
			message: normalized.message,
			stack: normalized.stack?.slice(0, STACK_TRACE_CAPS.MAX_CHARS),
			componentName,
			componentStack: errorInfo?.componentStack?.slice(0, STACK_TRACE_CAPS.MAX_CHARS),
			digest: errorInfo?.digest,
		},
	});
}
